import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export type Consultation = {
    id: number;
    patient_id: number;
    color_code: number | null;
    consultation_id: number;
    created_at: string;
    creator_alias: string;
    is_draft: boolean;
    last_event: string;
    links: {
        rel: string;
        href: string;
    }[];
    notes: string | null;
    is_testing: boolean;
    patient_first_name: string | null;
    patient_last_name: string | null;
    patient_age: { value: number; unit: string } | null;
    reminder_at: string | null;
    reminder_at_formatted: string | null;
    station_vitals: string[]; // ex: ["nibp", "ecg"]
    status: string;
    status_translated: string;
    tenant_name: string;
    unread: boolean;
    workflow: string;
    workflow_translated: string;
};

export type ConsultationsResponse = {
  consultations: Consultation[];
  draw: number;
  recordsFiltered: number;
  recordsTotal: number;
};

export type SearchFilter = {
  condition: string;
  label: string;
  checked: boolean;
}

const ENDPOINTS = {
  search: 'api/consultations/',
};

export const consultationsApi = createApi({
  reducerPath: 'consultationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/',
    prepareHeaders: (headers) => {
      headers.set('Accept', 'application/json');
      headers.set('X-CSRF-Token', window.CSRFToken); // Needed for POST requests
      return headers;
    },
  }),
  tagTypes: ['Consultations'],
  endpoints: (builder) => ({
    searchConsultations: builder.query<ConsultationsResponse, { search?: string; limit?: number; pageOffset?: number; sortModel?: GridSortModel, filters?: SearchFilter[], columnFilterModel?: GridFilterModel }>({
      query: ({ search, limit, pageOffset, sortModel, filters, columnFilterModel }) => {
        const params = new URLSearchParams();
        if (search) {
          params.append('_q', search);
        }
        if (limit) {
          params.append('limit', limit.toString());
        }
        if (pageOffset !== undefined) {
          params.append('offset', pageOffset.toString());
        }
        if (sortModel) {
          const formatedSortingRules = [];
          sortModel.forEach((sortingRule) => {
            const { field, sort: sortType } = sortingRule;
            if (sortType) {
              formatedSortingRules.push((sortType === 'desc' ? '-' : '') + field); // ex: "-created_at".
            }
          });
          if (formatedSortingRules.length > 0) {
            params.append('_sort', formatedSortingRules.join(','));
          }
        }
        // Not sure of this, could be a cleaner way to do this ?
        // Splicing is needed to format valid URL params.
        // Maybe we can improve the endpoint, possibly accept filters as JSON in request body.
        if (filters) {
          filters.filter(f => !f.checked).forEach(({ condition }) => {
            const neqIndex = condition.indexOf('!='); // "Not Equal" index
            if (neqIndex !== -1) {
              params.append(condition.slice(0, neqIndex) + '!', condition.slice(neqIndex + 2)); // Convert "field!=value" to valid query params format.
            } else {
              const eqIndex = condition.indexOf('='); // "Equal" index
              if (eqIndex !== -1) {
                params.append(condition.slice(0, eqIndex), condition.slice(eqIndex + 1)); // Convert "field=value" to valid query params format.
              }
            }
          });
        }
        if (columnFilterModel && columnFilterModel.items.length > 0) {
          columnFilterModel.items.forEach(({ field, operator, value }) => {
            if (!value) {
              return;
            } // Skip empty/unset filter values
            if (operator === 'contains') {
              params.append(field + '~', value);
            } else if (operator === 'equals') {
              params.append(field, value);
            } else if (operator === 'doesNotEqual') {
              params.append(field + '!', value);
            } else if (operator === 'isAnyOf') {
              // value is an array of strings when operator is isAnyOf — join as field=v1,v2,v3
              const values: string[] = Array.isArray(value) ? value : value.split(',');
              if (values.length > 0) {
                params.append(field + '__in', values.join(','));
              }
            }
          });
        }
        return `${ENDPOINTS.search}?${params.toString()}`;
      },
      providesTags: ['Consultations'],
    }),
    editConsultationReminders: builder.mutation<void, { reminderUrl: string; reminderAt: string | null; colorCode: number | null; notes: string | null; is_testing: boolean }>({
      query: ({ reminderUrl, reminderAt, colorCode, notes, is_testing }) => {
        const body = new URLSearchParams();

        // Simulate FormData.
        if (reminderAt !== undefined) {
          body.append('consultation-reminder', reminderAt ?? '');
        }
        if (colorCode !== undefined) {
          body.append('consultation-color_code', colorCode !== null ? String(colorCode) : '');
        }
        if (notes !== undefined) {
          body.append('consultation-notes', notes ?? '');
        }
        if (is_testing) {
          body.append('consultation-is_testing', 'on');
        }
        return {
          url: reminderUrl,
          method: 'POST',
          body: body.toString(),
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          credentials: 'same-origin',
        };
      },
      invalidatesTags: ['Consultations'], // Invalidate consultations cache to refetch updated data after editing reminders.
    }),
    // TODO: Refactor other consultation endpoints to RTKQ when possible/needed.
  }),
});

export const {
  useSearchConsultationsQuery,
  useEditConsultationRemindersMutation,
} = consultationsApi;
